import {
  CREATOR_MEMBERSHIP_GRACE_DAYS,
  CREATOR_MEMBERSHIP_MAX_FREE_TIERS,
  CREATOR_MEMBERSHIP_MAX_PAID_TIERS,
  CREATOR_MEMBERSHIP_PUBLIC_BUTTON_MAX_LENGTH,
} from './membership-types';

export type MembershipGoLiveInput = {
  title?: string | null;
  slug?: string | null;
  heroHeadline?: string | null;
  heroImageUrl?: string | null;
  servicesCount: number;
  publishedTierCount: number;
  validPublishedTierCount?: number;
  connectReady: boolean;
  hasPaidTier: boolean;
};

export type MembershipGoLiveChecklistItem = {
  id: string;
  label: string;
  ok: boolean;
  required: boolean;
};

export function slugifyMembershipTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function evaluateMembershipGoLiveChecklist(
  input: MembershipGoLiveInput,
): MembershipGoLiveChecklistItem[] {
  return [
    {
      id: 'page_identity',
      label: 'Membership page name and URL',
      ok: Boolean(input.title?.trim() && input.slug?.trim()),
      required: true,
    },
    {
      id: 'hero_headline',
      label: 'Hero headline',
      ok: Boolean(input.heroHeadline?.trim()),
      required: true,
    },
    {
      id: 'hero_image',
      label: 'Hero image',
      ok: Boolean(input.heroImageUrl?.trim()),
      required: true,
    },
    {
      id: 'services',
      label: 'At least one service or perk',
      ok: input.servicesCount >= 1,
      required: true,
    },
    {
      id: 'tiers',
      label: 'At least one valid active Membership tier with benefits',
      ok: (input.validPublishedTierCount ?? input.publishedTierCount) >= 1,
      required: true,
    },
    {
      id: 'connect',
      label: 'Stripe Connect ready for paid tiers',
      ok: !input.hasPaidTier || input.connectReady,
      required: input.hasPaidTier,
    },
  ];
}

export function canMembershipGoLive(input: MembershipGoLiveInput): boolean {
  return evaluateMembershipGoLiveChecklist(input)
    .filter((item) => item.required)
    .every((item) => item.ok);
}

export function computeMembershipGraceEndsAt(from: Date): Date {
  const end = new Date(from);
  end.setDate(end.getDate() + CREATOR_MEMBERSHIP_GRACE_DAYS);
  return end;
}

export type MembershipEntitlementInput = {
  status: 'active' | 'past_due' | 'cancelled';
  stripeBillingStatus?: string | null;
  graceEndsAt?: Date | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: Date | null;
};

export function grantsCreatorMembershipEntitlement(
  input: MembershipEntitlementInput,
  now = new Date(),
): boolean {
  if (input.status === 'active') return true;
  if (input.status === 'past_due') {
    if (input.graceEndsAt && input.graceEndsAt.getTime() > now.getTime()) return true;
    return false;
  }
  if (input.status === 'cancelled' && input.cancelAtPeriodEnd && input.currentPeriodEnd) {
    return input.currentPeriodEnd.getTime() > now.getTime();
  }
  return false;
}

export function validatePublicButtonLabel(label: string): { ok: boolean; message?: string } {
  const trimmed = label.trim();
  if (!trimmed) return { ok: false, message: 'Label is required' };
  if (trimmed.length > CREATOR_MEMBERSHIP_PUBLIC_BUTTON_MAX_LENGTH) {
    return {
      ok: false,
      message: `Label must be ${CREATOR_MEMBERSHIP_PUBLIC_BUTTON_MAX_LENGTH} characters or fewer`,
    };
  }
  return { ok: true };
}

export function assertTierLimits(counts: { free: number; paid: number }): {
  ok: boolean;
  message?: string;
} {
  if (counts.free > CREATOR_MEMBERSHIP_MAX_FREE_TIERS) {
    return { ok: false, message: `At most ${CREATOR_MEMBERSHIP_MAX_FREE_TIERS} free tier allowed` };
  }
  if (counts.paid > CREATOR_MEMBERSHIP_MAX_PAID_TIERS) {
    return { ok: false, message: `At most ${CREATOR_MEMBERSHIP_MAX_PAID_TIERS} paid tiers allowed` };
  }
  return { ok: true };
}

export function computeMembershipMrrCents(
  subscriptions: Array<{
    status: string;
    monthlyPriceCents: number | null;
    stripeBillingStatus?: string | null;
    graceEndsAt?: Date | null;
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: Date | null;
  }>,
  now = new Date(),
): number {
  return subscriptions.reduce((sum, row) => {
    if (!row.monthlyPriceCents) return sum;
    if (
      !grantsCreatorMembershipEntitlement(
        {
          status: row.status as 'active' | 'past_due' | 'cancelled',
          stripeBillingStatus: row.stripeBillingStatus,
          graceEndsAt: row.graceEndsAt,
          cancelAtPeriodEnd: row.cancelAtPeriodEnd,
          currentPeriodEnd: row.currentPeriodEnd,
        },
        now,
      )
    ) {
      return sum;
    }
    return sum + row.monthlyPriceCents;
  }, 0);
}

export function countMembershipActiveMembers(
  subscriptions: Array<{
    status: string;
    stripeBillingStatus?: string | null;
    graceEndsAt?: Date | null;
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: Date | null;
  }>,
  now = new Date(),
): number {
  return subscriptions.filter((row) =>
    grantsCreatorMembershipEntitlement(
      {
        status: row.status as 'active' | 'past_due' | 'cancelled',
        stripeBillingStatus: row.stripeBillingStatus,
        graceEndsAt: row.graceEndsAt,
        cancelAtPeriodEnd: row.cancelAtPeriodEnd,
        currentPeriodEnd: row.currentPeriodEnd,
      },
      now,
    ),
  ).length;
}

export type MembershipTierFeePreview = {
  priceCents: number;
  platformFeePercent: number;
  platformFeeCents: number;
  youKeepCents: number;
};

export function computeMembershipTierFeePreview(
  priceCents: number,
  platformFeePercent: number,
): MembershipTierFeePreview | null {
  if (priceCents <= 0) return null;
  const platformFeeCents =
    platformFeePercent > 0 ? Math.round((priceCents * platformFeePercent) / 100) : 0;
  return {
    priceCents,
    platformFeePercent,
    platformFeeCents,
    youKeepCents: Math.max(0, priceCents - platformFeeCents),
  };
}

export function validatePaidMembershipTierPrice(monthlyPriceCents?: number | null): {
  ok: boolean;
  message?: string;
} {
  if (monthlyPriceCents == null || monthlyPriceCents <= 0) {
    return { ok: false, message: 'Paid tiers require a monthly price greater than zero' };
  }
  return { ok: true };
}

export function canArchiveMembershipTier(input: { publishedTierCount: number }): {
  ok: boolean;
  message?: string;
} {
  if (input.publishedTierCount <= 1) {
    return {
      ok: false,
      message: 'You need at least one published tier. Add another tier before archiving this one.',
    };
  }
  return { ok: true };
}

export function canRestoreMembershipTier(input: {
  billingType: 'free' | 'paid' | string;
  publishedFreeCount: number;
  publishedPaidCount: number;
}): { ok: boolean; message?: string } {
  const counts = {
    free: input.publishedFreeCount,
    paid: input.publishedPaidCount,
  };
  if (input.billingType === 'free') counts.free += 1;
  if (input.billingType === 'paid') counts.paid += 1;
  return assertTierLimits(counts);
}
